/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable, Injector } from '@angular/core';
import { appConstants } from '@app/common/app-constants';
import { Logger } from '@core/lib/services';
import { UtilService } from '@app/util/util.service';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { DiService } from '@app/di/di.service';
import { DiConfigService } from '@app/di/di-config.service';
import { ViewerIntegrationService } from '@app/di/integrations/viewer/viewer-integration.service';
import { ReportIntegrationService } from '@app/di/integrations/reporting/report-integration.service';
import { WfmContextService } from '@app/di/integrations/wfm-context.service';
import { SignOutService } from '@app/common/sign-out.service';
import { RISCVService } from '@app/di/integrations/external-apps/riscv/riscv.service';
import { DigitalAssistantService } from '@app/di/integrations/digital-assistant/digital-assistant.service';

const log = new Logger('IntegrationsService');

@Injectable({
  providedIn: 'root'
})
export class IntegrationsService {
  constructor(
    private readonly injector: Injector,
    private readonly util: UtilService,
    private readonly translate: TranslateService,
    private readonly notify: NotifyService,
    private readonly signOutService: SignOutService,
    private readonly diConfigService: DiConfigService,
    private readonly integrationStatusService: IntegrationStatusService,
    private readonly diService: DiService,
    private readonly viewerIntegrationService: ViewerIntegrationService,
    private readonly reportIntegrationService: ReportIntegrationService,
    private readonly risCVService: RISCVService,
    private readonly digitalAssistantService: DigitalAssistantService
  ) {}

  init() {
    if (!this.isDIDisabled()) {
      // Registering for cleanup on signout
      this.signOutService.registerCallback(this.cleanup);

      // Initialization
      // WfmContextService - For external launch & single instance in same client
      this.injector.get(WfmContextService).init();

      // Viewer
      this.viewerIntegrationService.initialize();

      // Reporting
      this.reportIntegrationService.initialize();

      // RISi Content Viewer
      this.risCVService.init();

      // Digital Assistant
      const daConfig = this.diConfigService.integrationConfig?.Da;
      log.info(`DA config check: Da=${!!daConfig}, url=${daConfig?.url || 'N/A'}`);
      console.log('[DA-DEBUG] IntegrationsService.init() — DA config:', daConfig ? JSON.stringify(daConfig) : 'NOT PRESENT');
      if (daConfig) {
        console.log('[DA-DEBUG] IntegrationsService.init() — calling digitalAssistantService.init()');
        this.digitalAssistantService.init();
      } else {
        console.warn('[DA-DEBUG] IntegrationsService.init() — DA config is MISSING, DA icon will NOT show');
      }
    }
  }

  initExternalLaunchWorkflow(diConfigs, launchParams) {
    log.debug('External Launch Config Initialized');

    const uvip = launchParams[appConstants.uvip].split(':');
    diConfigs['server'] = uvip[0];
    let port = '443';
    if (uvip.length > 1) {
      port = uvip[1].trim() === '' ? port : uvip[1];
    }
    diConfigs['port'] = port;
    diConfigs['login'] = launchParams[appConstants.username];

    this.diConfigService.initData(diConfigs);
    this.injector.get(WfmContextService).init(true, launchParams[appConstants.messageId]);
  }

  // Function to check, whether the desktop integration is disabled or not,
  //   by retrieving the parameter fromcurrent url.
  private isDIDisabled(): boolean {
    const isDisabled = this.util.getQueryParam('diDisable');
    console.log(`[DA-DEBUG] isDIDisabled() — diDisable query param: ${isDisabled}`);
    if (isDisabled) {
      console.warn('[DA-DEBUG] isDIDisabled() — Desktop integration DISABLED via query param, DA will NOT initialize');
      this.notify.warning(
        this.translate.instant('di.desktopIntegration.warning.integrationDisabled'),
        this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
      );
      this.integrationStatusService.disableDesktopIntegration();
    }
    this.diService.setIntegrationStatus(!isDisabled);
    log.info('Integration module : ' + (isDisabled ? 'Disabled' : 'Enabled'));
    return isDisabled;
  }

  private readonly cleanup = () => {
    log.info('Start cleanup, sending direct xml to all the plugins to avoid request queuing in logout scenario');
    // Set logout initiated status.
    this.diService.setLogoutInitiatedStatus();
    log.info('End cleanup');
    return true;
  };
}
